<template>
  <div class="form">
    <transfer-type-toggle
      v-model="transferType"
      :with-car-rental="!inIframe"
      @input="$emit('update:type', $event)"
    />
    <q-form @submit="submit()">
      <div class="q-pa-sm">
        <div class="q-gutter-sm">
          <autocomplete-google-map
            ref="fromAutocomplete"
            v-model="transfer.from"
            class="autocomplete fs-14"
            bg-color="white"
            :label="$t('placeholder.from')"
            :clearable="$q.screen.gt.sm"
            :lazy-rules="lazyRules"
            popup-content-class="fs-14"
            :options-dense="inIframe"
            hide-bottom-space
            hide-dropdown-icon
            dense
            :rules="[v => requiredField(v, $t('error.required_address'))]"
            outlined
            with-choose-on-map-btn
            @choose-on-map="submit(null, 'from')"
            @select="save"
          />
          <div
            v-if="showSwapBtn"
            class="absolute full-width q-mt-none"
          >
            <q-btn
              class="swap"
              color="white"
              size="6px"
              text-color="black"
              round
              unelevated
              @click="swap"
            >
              <q-icon
                name="autorenew"
                size="22px"
              />
            </q-btn>
          </div>
          <q-slide-transition>
            <div v-if="!isCarRental && !isHourly">
              <autocomplete-google-map
                ref="toAutocomplete"
                v-model="transfer.to"
                class="autocomplete fs-14"
                bg-color="white"
                :label="$t('placeholder.to')"
                :clearable="$q.screen.gt.sm"
                :lazy-rules="lazyRules"
                popup-content-class="fs-14"
                :options-dense="inIframe"
                hide-bottom-space
                hide-dropdown-icon
                dense
                outlined
                :rules="[v => requiredField(v, $t('error.required_address'))]"
                with-choose-on-map-btn
                @choose-on-map="submit(null, 'to')"
                @select="save"
              />
            </div>
          </q-slide-transition>
          <q-slide-transition>
            <div
              v-if="expandPricesOnShortForm || showPrices"
              class="additional-fields"
            >
              <number-input
                v-model.number="transfer.passenger_offered_price"
                class="number-input fs-14 q-mb-sm"
                :label="$t('transfer.offer_your_price')"
                :max="99999999"
                bg-color="white"
                outlined
                clear-icon="close"
                clearable
                dense
              >
                <template #prepend>
                  <div>
                    <q-select
                      v-model="values.currency"
                      class="currency q-pl-sm fs-14"
                      :options="configs.supported_currencies"
                      :virtual-scroll-slice-size="configs.supported_currencies.length"
                      option-label="symbol"
                      option-value="iso_code"
                      popup-content-class="fs-14"
                      dense
                      emit-value
                      map-options
                      borderless
                      @input="updateAccountSettings"
                    >
                      <template #option="{ itemProps, itemEvents, opt }">
                        <q-item
                          v-bind="itemProps"
                          v-on="itemEvents"
                        >
                          <q-item-section avatar>{{ opt.symbol }}</q-item-section>
                          <q-item-section>{{ opt.title }}</q-item-section>
                        </q-item>
                      </template>
                    </q-select>
                  </div>
                </template>
              </number-input>
              <div class="transports row justify-center q-col-gutter-sm">
                <template v-for="{ id, delivery } in configs.transport_types">
                  <div
                    v-if="isDelivery === !!delivery"
                    :key="id"
                    :class="isDelivery ? 'col-6' : 'col-4 col-sm-6'"
                    :style="{
                      order: transportTypesOrder[id],
                    }"
                  >
                    <transport-type-tile
                      class="full-width"
                      :price="prices && prices[id]"
                      :type="id"
                      @input="submit(id)"
                    />
                  </div>
                </template>
                <div
                  v-if="isDelivery"
                  class="col-6"
                >
                  <a
                    class="text-black"
                    href="https://gettransport.com/?utm_source=gettransfer&utm_medium=referal&utm_campaign=delivery"
                    target="_blank"
                  >
                    <transport-type-tile
                      class="full-width"
                      type="cargo"
                      is-cargo
                    />
                  </a>
                </div>
              </div>
            </div>
          </q-slide-transition>
        </div>
      </div>
      <q-btn
        v-if="isCarRental"
        class="full-width"
        color="positive"
        type="a"
        :href="externalLink"
        target="_blank"
        :label="$t('button.get_offers')"
        icon-right="open_in_new"
      />
      <q-btn
        v-else
        class="full-width"
        color="positive"
        type="submit"
        :label="$t('button.get_offers')"
        @click="lazyRules = false"
      />
    </q-form>
  </div>
</template>

<script>
import {
  AccountSettingsMethods,
  AddressInputHelpers,
  GetPlaceDetailsFromQuery,
  RequiredField,
  TransferForms,
  ExtarnalLinks,
} from '~mixins';

import AutocompleteGoogleMap from '~components/AutocompleteGoogleMap';
import NumberInput from '~components/NumberInput';
import TransferTypeToggle from '~components/TransferTypeToggle';
import TransportTypeTile from '~components/TransportTypeTile';

export default {
  name: 'ShortTransferForm',
  components: {
    AutocompleteGoogleMap,
    NumberInput,
    TransferTypeToggle,
    TransportTypeTile,
  },
  mixins: [
    AccountSettingsMethods,
    AddressInputHelpers,
    GetPlaceDetailsFromQuery,
    RequiredField,
    TransferForms,
    ExtarnalLinks,
  ],
  props: {
    type: {
      type: String,
      default: 'route',
    },
    inIframe: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const expand = this.$q.cookies.get('expandPricesOnShortForm');

    return {
      lazyRules: 'ondemand',
      transferType: this.type,
      prices: null,
      showPrices: this.$route.query.transfer_type
        ? this.$route.query.transfer_type === this.$q.cookies.get('transferType') && expand
        : expand,
      transfer: {
        from: {},
        to: {},
        passenger_offered_price: null,
        transport_type_ids: [],
      },
    };
  },
  computed: {
    paramsToWatch() {
      return [
        this.transferType,
        this.transfer.from.point,
        this.transfer.to.point,
        this.values.currency,
      ].join();
    },
  },
  watch: {
    expandPricesOnShortForm() {
      this.showPrices = false;
    },
    isHourly(value) {
      if (value) {
        this.prices = null;
        this.transfer.to = {};
        this.save();
      }
    },
    isDelivery(value) {
      if (value) {
        try {
          window.gtag('event', 'delivery', {
            event_category: 'tab',
          });
        } catch {}
      }
    },
    isCarRental(value) {
      if (value) {
        try {
          window.gtag('event', 'car-rent', {
            event_category: 'tab',
          });
        } catch {}
        this.prices = null;
        this.transfer.to = {};
        this.save();
      }
    },
    paramsToWatch() {
      this.updatePrices();
    },
  },
  beforeDestroy() {
    window.removeEventListener('google_init', this.initRoute);
  },
  mounted() {
    const {
      from_name,
      from_place_id,
      to_name,
      to_place_id,
    } = this.$route.query;

    let {
      from = {},
      to = {},
    } = this.$q.localStorage.getItem('saved_transfer')?.transfer || {};

    if (from_name || from_place_id) {
      from = {
        name: from_name || '',
        place_id: from_place_id || '',
      };
    }

    if (to_name || to_place_id) {
      to = {
        name: to_name || '',
        place_id: to_place_id || '',
      };
    }

    this.transfer.to = to;
    this.transfer.from = from;

    if (window.googleMapInstance) this.initRoute();
    window.addEventListener('google_init', this.initRoute);
  },
  methods: {
    async initRoute() {
      this.initGeocode();

      const { from, to } = this.transfer;

      if (!from.point && (from.name || from.place_id)) {
        this.transfer.from = await this.getPlaceDetailsFromQuery(from);
      }
      if (!to.point && (to.name || to.place_id)) {
        this.transfer.to = await this.getPlaceDetailsFromQuery(to);
      }
    },
    async updatePrices() {
      try {
        this.prices = (await this.$axios('/api/route_info', {
          params: {
            points: [this.transfer.from.point, this.transfer.to.point],
            countries: [this.transfer.from.country, this.transfer.to.country],
            currency: this.values.currency,
            delivery: this.isDelivery,
            duration: this.isHourly ? 1 : undefined,
            with_prices: true,
          },
        })).data.prices;
      } catch (err) {
        console.error(err);
      }
    },
    save() {
      this.$q.localStorage.set('saved_transfer', {
        transfer: this.transfer,
      });
    },
    submit(type, pointType) {
      const { from, to, ...transfer } = this.transfer;

      if (pointType) {
        this.$route.params.pointToChooseOnMap = pointType;
      }

      if (type) {
        transfer.transport_type_ids.push(type);
      }

      const routeParams = {
        name: 'transfer.new',
        params: this.$route.params,
        query: {
          transfer_type: this.transferType,
          from_name: from.name,
          from_place_id: from.place_id,
          to_name: to.name,
          to_place_id: to.place_id,
          transfer,
        },
      };

      if (this.inIframe) {
        this.$emit('submit', routeParams);
      } else {
        this.$router.push(routeParams);
      }

      try {
        window.gtag('event', 'calculate1', { event_action: 'calculate' });
      } catch (e) {}
      try {
        window.gtYaCounter('reachGoal', 'calculate-yandex');
      } catch (e) {}
      try {
        window.uetq = window.uetq || [];
        window.uetq.push({
          ec: 'calculate-bing1',
          ea: 'calculate-bing2',
          el: 'calculate-bing3',
          ev: 1,
        });
      } catch (e) {}
    },
    swap() {
      [this.transfer.from, this.transfer.to] = [this.transfer.to, this.transfer.from];
    },
  },
};
</script>

<style lang="scss" scoped>
.currency::v-deep {
  border-right: 1px solid rgba(#000, .24);
  .q-field__native {
    padding: 0;
  }
  .q-field__control {
    &:before, &:after {
      display: none;
    }
  }
}

.form {
  background-color: rgba(#fff, .8);
  border-radius: 24px;
  z-index: 1;
}

.swap {
  border: 1px solid #ddd;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99;
}

.number-input::v-deep > .q-field__inner > .q-field__control {
  padding-left: 0;
}

@media screen and (max-width: $breakpoint-xs-max) {
  .transports > div {
    order: unset !important;
  }
}

.tile ::v-deep {
  .info {
    margin: 0 4px;
    min-width: 64px;
  }
  .price-block {
    font-size: 10px !important;
  }
}
</style>
